import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, NavLink, useLocation, useMatch, useNavigate } from "react-router-dom";
import { INDEX_URL, CUST, icons, logos, FLAVOR } from "../../helpers/constants";
import LanguagePicker from "../LanguagePicker";
import { useTranslation } from "react-i18next";
import * as request from "../../services/FetchService";
import InAppModal from "../Modals/InAppModal";
import { SidebarData } from "../Sidebar/SidebarData";
import { useSidebarContext } from "./SiderbarContext";
import { toggleTheme } from "../../assets/css/theme";
import { AnimatePresence, motion } from "framer-motion";
import { useIsMobile } from "../../helpers/customHooks";

const SidebarNew = (props) => {
	const { t } = useTranslation();
	const MOBILE = useIsMobile();
	const [hoverpageTags, setHoverPageTags] = useState("");
	const [showModal, setShowModal] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { topbarContent, setTopbarContent, actionbarContent, setActionbarContent, topbarTwoContent, setTopbarTwoContent } = useSidebarContext();
	const [pageName, setPageName] = useState("");
	const [onHover, setOnHover] = useState(false);
	const [outerShowLang, setOuterShowLang] = useState(false);
	const [tema, setTema] = useState(() => document.documentElement.getAttribute("data-theme") || "light");
	const [notifications, setNotifications] = useState({
		alarmicon: false,
		messageicon: false,
		startupmessage: { show: false, newuser: false },
	});

	// ---- New bits for robust polling ----
	const intervalRef = useRef(null);
	const inFlightRef = useRef(false);
	const [isVisible, setIsVisible] = useState(() => !document.hidden);

	const getNewMessage = useCallback(async () => {
		try {
			const response = await request.get("account/messagenotification/check");
			if (response && response.data && response.data.successful) {
				const newData = response.data.data || {};
				setNotifications((prev) => ({
					...prev,
					alarmicon: newData.alarmicon,
					messageicon: newData.messageicon,
					startupmessage: {
						...prev.startupmessage,
						show: newData.startupmessage && "show" in newData.startupmessage ? newData.startupmessage.show : prev.startupmessage.show,
						newuser: newData.startupmessage && "newuser" in newData.startupmessage ? newData.startupmessage.newuser : prev.startupmessage.newuser,
					},
				}));
			}
		} catch (e) {
			// Optional: log or backoff on errors
			// console.error(e);
		}
	}, []);

	// Prevent overlapping API calls if one run is slow
	const safeFetch = useCallback(async () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		try {
			await getNewMessage();
		} finally {
			inFlightRef.current = false;
		}
	}, [getNewMessage]);

	// Track tab visibility
	useEffect(() => {
		const onVisibility = () => setIsVisible(!document.hidden);
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, []);

	// Single authoritative polling effect
	useEffect(() => {
		// Conditions under which we poll
		const shouldPoll = isVisible && !showModal;

		// Always clear any existing interval before deciding
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (shouldPoll) {
			// Immediate refresh so the UI is fresh when conditions become true
			safeFetch();
			// Then poll every 60s
			intervalRef.current = setInterval(safeFetch, 60000);
		}

		// Cleanup on dependency change or unmount
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isVisible, showModal, safeFetch]);

	// One-off fetch when the modal closes (optional; the main effect already does an immediate fetch on resume)
	useEffect(() => {
		if (!showModal) safeFetch();
	}, [showModal, safeFetch]);

	const findPageName = () => {
		const currPage = SidebarData.find((page) => page.path === location.pathname) ?? "";
		setPageName(currPage.title ?? "");
	};
	// Clear alarm dot when user visits /events
	useEffect(() => {
		setTopbarContent(null);
		setActionbarContent(null);
		if (location.pathname === "/events") {
			setNotifications((prev) => ({ ...prev, alarmicon: false }));
		}
		if(location.pathname === "/messages"){
			setNotifications((prev) => ({ ...prev, messageicon: false }));
		}
		findPageName();
	}, [location.pathname]);

	const onToggleTheme = () => {
		const curr = toggleTheme();
		setTema(curr);
	};

	const darkmodeIconSrc = tema === "dark" ? icons.sun : icons.moon;
	const isActivateRoute = useMatch("/activate/:token");
	return (
		<>
			<div className="d-flex position-absolute top-0 start-0 w-100">
				<motion.div
					className={`glass-effect margin-fixer rounded32 d-flex actionbar margin-x-20 d-flex flex-column flex-shrink-0`}
					style={{ zIndex: 1040, willChange: "height", overflow: "hidden", boxSizing: "border-box" }}
					initial={false}
					animate={{ height: actionbarContent ? "" : "64px" }}
					transition={{ duration: 0.5, ease: [0.25, 1, 0.25, 1] }}
				>
					<motion.div className="d-flex justify-self-center align-items-center justify-content-between position-relative py-2 h-100 mx-3">
						{!topbarContent && (
							<>
								<div />
								<h4 className="mega-center">{t(pageName)}</h4>
								<div />
							</>
						)}
						{topbarContent?.left === "burger" ? <div /> : topbarContent?.left || <div />}
						{topbarContent?.center || <div />}
						{topbarContent?.right || <div />}
					</motion.div>
					<AnimatePresence initial={false}>{actionbarContent && actionbarContent}</AnimatePresence>
				</motion.div>
				{(location.pathname === "/settings" || location.pathname === "/messages" || location.pathname === "/subscriptions" || location.pathname === "/devices" || isActivateRoute) && topbarTwoContent && (
					<motion.div layout="size" className="glass-effect margin-fixer rounded32 d-flex actionbar-settings flex-column flex-fill" style={{ zIndex: 1040, overflow: "hidden", boxSizing: "border-box" }} transition={{ duration: 0.5, ease: [0.25, 1, 0.25, 1] }}>
						<div className="d-flex align-items-center justify-content-between position-relative py-2 mx-3" style={{ height: 64 }}>
							{topbarTwoContent?.left || <div />}
							{topbarTwoContent?.center || <div />}
							{topbarTwoContent?.right || <div />}
						</div>

						<AnimatePresence initial={false}>
							{topbarTwoContent?.action && (
								<motion.div key="action" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }} transition={{ duration: 0.5, ease: [0.25, 1, 0.25, 1] }}>
									{topbarTwoContent.action}
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				)}
			</div>

			<motion.div
				className={`sidebar-new glass-effect d-flex flex-column position-fixed py-5 scrollable-y ${isActivateRoute ? "d-none": ""}`}
				style={{ zIndex: 1040, overflow: "hidden", boxSizing: "border-box" }}
				animate={{ width: onHover ? "240px" : "64px" }}
				transition={{ duration: 0.35, ease: [0.25, 1, 0.25, 1] }}
				onMouseEnter={() => setOnHover(true)}
				onMouseLeave={() => setOnHover(false)}
			>
				<div className="d-flex flex-column h-100">
					<div className="d-flex align-items-center nav-row mb-4">
						<div className="icon-cell">
							<img src={logos.small} alt="" className="size_25 cursor-pointer" onClick={() => navigate("/devices")} />
						</div>
						<motion.div
							className="label"
							initial={false}
							animate={{
								maxWidth: onHover ? 999 : 0,
								x: onHover ? 0 : -8,
							}}
							transition={{ duration: 0.25 }}
						>
							<img src={logos.big} alt="" className="cursor-pointer w-75" onClick={() => navigate("/devices")} />
						</motion.div>
					</div>

					{SidebarData.map(
						(item, i) =>
							item.path !== "/map" && (
								<NavLink key={i} to={item.path} className="text-decoration-none">
									<div className="d-flex align-items-center nav-row mb-3">
										<div className="icon-cell position-relative">
											{item.path === "/events" && notifications?.alarmicon && <span className="position-absolute dot bg-offline top-0 mt-0 end-0 me-3" />}
											{item.path === "/messages" && notifications?.messageicon && <span className="position-absolute dot bg-offline top-0 mt-0 end-0 me-3" />}
											<div className={`full-rounded ${location.pathname === item.path ? "bg-gradient" : ""} d-flex justify-content-center align-items-center p-2`}>
												<img src={item.icon_inactive} className={`${location.pathname === item.path ? "white-white-icon" : "grey-blue-icon"} cursor-pointer size_20`} alt="..." />
											</div>
										</div>
										<motion.div
											className="label"
											initial={false}
											animate={{
												maxWidth: onHover ? 999 : 0,
												x: onHover ? 0 : -8,
											}}
											transition={{ duration: 0.25 }}
										>
											<h4 className="bold grey-blue">{t(item.title)}</h4>
										</motion.div>
									</div>
								</NavLink>
							),
					)}

					<div className="mt-auto">
						<div className="d-flex align-items-center nav-row">
							<div className="icon-cell position-relative">
								<LanguagePicker outerShowLang={outerShowLang} />
							</div>
							<motion.div onClick={() => setOuterShowLang(!outerShowLang)} className="label cursor-pointer" initial={false} animate={{ maxWidth: onHover ? 999 : 0, x: onHover ? 0 : -8 }}>
								<span className="fw-bold">{t("sidebar.language")}</span>
							</motion.div>
						</div>
						{process.env.REACT_APP_ASSETS === "swetrack" && (
							<div className="d-flex align-items-center nav-row mt-2">
								<img onClick={onToggleTheme} src={darkmodeIconSrc} className={`grey-blue-icon icon-cell position-relative cursor-pointer size_20`} alt="..." />
								<motion.div onClick={onToggleTheme} className="label cursor-pointer" initial={false} animate={{ maxWidth: onHover ? 999 : 0, x: onHover ? 0 : -8 }}>
									<span className="fw-bold">{t("sidebar.theme")}</span>
								</motion.div>
							</div>
						)}
					</div>
				</div>
			</motion.div>
		</>
	);
};

export default SidebarNew;
